import type {Card} from "../cdx-models/Card";
import type {DerivedStatus} from "../cdx-models/field-definitions";
import {isPublicContext} from "./app-mode";

export const sortedStatusVals = [
  "unassigned",
  "assigned",
  "started",
  "snoozing",
  "blocked",
  "review",
  "done",
  "archived",
  "deleted",
  "hero",
  "doc",
] as const;

export type CardStatus = (typeof sortedStatusVals)[number];

const statusToSortVal: Record<string, number> = Object.fromEntries(
  sortedStatusVals.map((val, i) => [val, i])
);

// --- derivedStatus lists & sets ---

const docStatusList: DerivedStatus[] = ["doc", "heroDoc"];
export const docStatusSet = new Set<DerivedStatus>(docStatusList);

const doneStatusList: DerivedStatus[] = ["done", "archivedDone", "heroDone", "archivedHeroDone"];
export const doneStatusSet = new Set<DerivedStatus>(doneStatusList);

const archivedStatusList: DerivedStatus[] = [
  "archived",
  "archivedHero",
  "archivedDone",
  "archivedHeroDone",
];
export const archivedStatusSet = new Set<DerivedStatus>(archivedStatusList);

const heroStatusList: DerivedStatus[] = [
  "hero",
  "heroDone",
  "heroDoc",
  "archivedHero",
  "archivedHeroDone",
];
export const heroStatusSet = new Set<DerivedStatus | null>(heroStatusList);

/** derivedStatus values a card may be marked as done from. Blocked and review cards must be resolved first. */
export const completableStatusSet = new Set<DerivedStatus>([
  "assigned",
  "unassigned",
  "snoozing",
  "started",
  "hero",
]);

/** derivedStatus values for cards with active work remaining (countable minus done) */
export const incompleteStatusList: DerivedStatus[] = [
  "blocked",
  "review",
  "hero",
  "assigned",
  "unassigned",
  "started",
  "snoozing",
];

const nonCountableStatusList: DerivedStatus[] = [
  "archived",
  "archivedHero",
  "deleted",
  ...docStatusList,
];
export const nonCountableStatusSet = new Set<DerivedStatus>(nonCountableStatusList);

/** Card is not done, not archived, and not deleted — i.e. still actively present */
const inactiveStatusList: DerivedStatus[] = [...doneStatusList, ...archivedStatusList, "deleted"];
export const inactiveStatusSet = new Set<DerivedStatus>(inactiveStatusList);

/** derivedStatus values for cards that contribute to progress (not archived-incomplete, not deleted, not doc) */
export const countableStatusList: DerivedStatus[] = [...incompleteStatusList, ...doneStatusList];

export const countableAndDocStatusList: DerivedStatus[] = [
  ...countableStatusList,
  ...docStatusList,
];

/** derivedStatus values for active cards — not done, not archived, not deleted. Includes docs and heroes. */
export const activeStatusList: DerivedStatus[] = [...docStatusList, ...incompleteStatusList];

// --- derivedStatus → CardStatus mapping ---

export const derivedToCardStatus: Record<DerivedStatus, CardStatus> = {
  deleted: "deleted",
  archived: "archived",
  archivedHero: "archived",
  archivedDone: "done",
  archivedHeroDone: "done",
  doc: "doc",
  heroDoc: "doc",
  blocked: "blocked",
  review: "review",
  hero: "hero",
  heroDone: "done",
  assigned: "assigned",
  unassigned: "unassigned",
  started: "started",
  done: "done",
  snoozing: "snoozing",
};

// --- Card-level predicates (derivedStatus-based) ---

type DerivedStatusLike = {derivedStatus: DerivedStatus | "unknown"};

export const isCardDone = (c: DerivedStatusLike): boolean =>
  doneStatusSet.has(c.derivedStatus as DerivedStatus);

export const isCardArchived = (c: DerivedStatusLike): boolean =>
  archivedStatusSet.has(c.derivedStatus as DerivedStatus);

export const isCardDeleted = (c: DerivedStatusLike): boolean => c.derivedStatus === "deleted";

export const isCardDoc = (c: DerivedStatusLike): boolean =>
  docStatusSet.has(c.derivedStatus as DerivedStatus);

/** Card contributes to progress stats (not archived-incomplete, not deleted, not doc) */
export const isCardCountable = (c: DerivedStatusLike): boolean =>
  !nonCountableStatusSet.has(c.derivedStatus as DerivedStatus);

/** Card is not done, not archived, and not deleted */
export const isCardActive = (c: DerivedStatusLike): boolean =>
  !inactiveStatusSet.has(c.derivedStatus as DerivedStatus);

/** Card has active work remaining (countable and not done) */
export const isCardIncomplete = (c: DerivedStatusLike): boolean =>
  !nonCountableStatusSet.has(c.derivedStatus as DerivedStatus) &&
  !doneStatusSet.has(c.derivedStatus as DerivedStatus);

/** Any child card still has active work remaining */
export const areChildCardsIncomplete = (childCards: DerivedStatusLike[]): boolean =>
  childCards.some(isCardIncomplete);

// --- Moved from workflow-utils ---

export const isHeroCard = (card: Card): boolean =>
  heroStatusSet.has(card.derivedStatus as DerivedStatus);

// --- Moved from MilestoneOverviewComponents ---

export type ProgressStatus =
  | "done"
  | "review"
  | "blocked"
  | "assigned"
  | "unassigned"
  | "snoozing"
  | "started"
  | "hero";

export const derivedStatusToProgressStatus = (
  derived: DerivedStatus | null
): ProgressStatus | null => {
  if (derived === null) return null;
  if (doneStatusSet.has(derived)) return "done";
  if (nonCountableStatusSet.has(derived)) return null;
  return derived as ProgressStatus;
};

/**
 * Maps a DerivedStatus to its coarser CardStatus, suitable for theme/variant lookups.
 * Use this instead of `getStatusForCard(card)` — e.g. `getCardThemeStatus(card.derivedStatus)`.
 */
export const getCardThemeStatus = (ds: DerivedStatus | "unknown"): CardStatus =>
  derivedToCardStatus[ds as DerivedStatus] ?? "unassigned";

// --- getStatusForCard ---

const getStatusForCardLegacy = (card: Card): CardStatus => {
  if (card.visibility === "deleted") return "deleted";
  if (card.isDoc) return card.visibility === "archived" ? "archived" : "doc";
  if (isCardBlocked(card)) return "blocked";
  if (isCardInReview(card)) return "review";
  if (card.visibility === "archived" && card.status !== "done") return "archived";
  const status = card.$meta.get("status", "not_started");
  if (status === "not_started") {
    if (isHeroCard(card)) return "hero";
    return card.assignee ? "assigned" : "unassigned";
  }
  return status;
};

/**
 * @deprecated Use `getCardThemeStatus(card.derivedStatus)` for theme lookups,
 * or use `card.derivedStatus` directly with helpers like `isCardDone`, `isCardArchived`, etc.
 */
export const getStatusForCard = (card: Card): CardStatus => {
  if (isPublicContext) {
    // open mode needs live resolvable checks filtered by isPublic
    return getStatusForCardLegacy(card);
  }
  const ds = card.derivedStatus as DerivedStatus;
  return derivedToCardStatus[ds] ?? "unassigned";
};

export const statusForWorkflowItem = (card: Card): "deleted" | "workflowItem" => {
  if (card.visibility === "deleted") return "deleted";
  return "workflowItem";
};

export const isCardBlocked = (card: Card) =>
  card.$meta.exists("resolvables", {
    isClosed: false,
    context: "block",
    ...(isPublicContext && {isPublic: true}),
  });
export const isCardInReview = (card: Card) =>
  card.$meta.exists("resolvables", {
    isClosed: false,
    context: "review",
    ...(isPublicContext && {isPublic: true}),
  });

export const statusToLabel: Record<CardStatus, string> = {
  snoozing: "Snoozing",
  blocked: "Blocked",
  assigned: "Assigned",
  unassigned: "Unassigned",
  review: "Review",
  done: "Done",
  started: "Started",
  archived: "Archived",
  deleted: "Deleted",
  hero: "Hero card",
  doc: "Doc card",
};

// x cards are [PHRASE] used in metrics
export const statusToPhrase: Record<CardStatus, string> = {
  snoozing: "snoozing",
  blocked: "blocked",
  assigned: "assigned",
  unassigned: "unassigned",
  review: "in review",
  done: "done",
  started: "started",
  archived: "archived",
  deleted: "deleted",
  hero: "a Hero card",
  doc: "a Doc card",
};

export function statusSorter(status: CardStatus): number {
  return statusToSortVal[status];
}
